import { Api } from "@/api"
import Icon from "@/components/Icon"
import BootstrapButton from "@/components/form/BootstrapButton"
import { StateLoading } from "@/components/loading/StateLoading"
import { useLoading } from "@/composition/useLoading"
import { type TFSEntryMetadata } from "@/models"
import { useLoggedInUser } from "@/stores/loggedInUserStore"
import { faCirclePlus, faFilter } from "@fortawesome/free-solid-svg-icons"
import { computed, ref } from "vue"
import { defineComponent, inferProps, mergePromises, type ReactiveComponent } from "vue-utils"
import ShipmentResultsDisplay from "./ShipmentResultsDisplay"
import ShipmentsFilter from "./components/ShipmentsFilter"
import TFSInfo from "./components/TFSInfo"
import CreateShipmentForm from "./documents/CreateShipmentForm"
import * as Permissions from "./permissions"
import { setupShipmentsStore } from "./store"
import { bindShipmentsRequestToQuery } from "./useSearchShipments"

interface Props {
	tfs: TFSEntryMetadata
}

const ManageShipments: ReactiveComponent<Props> = (props) => {
	const store = setupShipmentsStore(props.tfs)
	bindShipmentsRequestToQuery(store.requestOptions)

	const { runAction } = useLoading()

	const loggedInUser = useLoggedInUser()

	let recipientSuggestions: string[] = []
	let nextShipmentNumber: number = NaN
	const creatingShipment = ref(false)

	const canCreateShipment = computed(() => Permissions.canCreateShipment(loggedInUser, store.tfs))

	async function handleCreate() {
		const data = await runAction(
			mergePromises({
				nextShipmentNumber: store.getNextShipmentNumber(),
				recipientSuggestions: Api.tfs.getRecipientSuggestions(store.tfs.id),
			})
		)
		nextShipmentNumber = data.nextShipmentNumber
		recipientSuggestions = data.recipientSuggestions
		creatingShipment.value = true
	}

	return () => (
		<>
			<h5 class="mb-1 flex items-center flex-wrap spacing-2">
				<span class="flex-grow-1">Filter Shipments</span>
				{canCreateShipment.value && (
					<BootstrapButton color="success" onClick={() => void handleCreate()}>
						<Icon icon={faCirclePlus} />
						<span>Create Shipment</span>
					</BootstrapButton>
				)}
				<BootstrapButton color="danger" onClick={() => store.resetSearchOptions()}>
					<Icon icon={faFilter} />
					<span>Reset Filters</span>
				</BootstrapButton>
			</h5>

			<ShipmentsFilter />
			<hr />

			<TFSInfo tfs={store.tfs} />
			<hr />

			<StateLoading stores={[store.shipmentsRef]}></StateLoading>
			{store.hasResults() && (
				<>
					<h5>Shipments</h5>
					<ShipmentResultsDisplay />
				</>
			)}

			{creatingShipment.value && (
				<CreateShipmentForm
					nextShipmentNumber={nextShipmentNumber}
					recipientSuggestions={recipientSuggestions}
					createShipment={async (shipmentNumber, files, recipients) => {
						const result = await store.createShipment({
							shipmentNumber,
							files,
							recipients,
						})
						creatingShipment.value = false
						return result
					}}
					cancel={() => (creatingShipment.value = false)}
				/>
			)}
		</>
	)
}

export default defineComponent(ManageShipments, inferProps<Props>())
